import React from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, Linkedin, Download, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';

const AndrePage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Andre Savala - Sales & Customer Support | CTC Waterworks"
        description="Connect with Andre Savala, Sales & Customer Support specialist at CTC Waterworks. Get his contact information and learn about CTC's agricultural water solutions."
        canonicalPath="/team/andre"
      />
      <Navbar />

      {/* Header Section */}
      <section className="pt-24 pb-8 bg-gradient-to-br from-slate-50 via-ctc-silver-light to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link to="/" className="inline-block mb-6">
              <img
                src="/CTC_Waterworks Logo_Blue_White.png"
                alt="CTC Waterworks"
                className="h-16 w-auto mx-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling;
                  if (fallback) (fallback as HTMLElement).style.display = 'block';
                }}
              />
              <div style={{ display: 'none' }} className="text-3xl font-bold text-ctc-blue">
                CTC Waterworks
              </div>
            </Link>
            <p className="text-ctc-blue font-semibold text-lg mb-8">Better Water = Better Profits</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
          >
            <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-6 border-4 border-green-200 shadow-xl">
              <img
                src="/Andre Savala.png"
                alt="Andre Savala"
                className="w-full h-full object-cover"
              />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-3">
              Andre Savala
            </h1>
            <p className="text-xl text-gray-600 font-semibold mb-2">Sales & Customer Support – CTC Waterworks</p>
            <p className="text-sm text-gray-500">Tap below to save my contact directly to your phone</p>
          </motion.div>

          {/* Contact Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-4 mb-12"
          >
            <a
              href="/Andre-Savala.R12.25.vcf"
              download
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-5 rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-lg text-lg"
            >
              <Download size={24} />
              <span>Add Andre to Your Contacts</span>
            </a>

            <a
              href="tel:+15099017476"
              className="w-full bg-white border-2 border-green-600 text-green-600 px-8 py-5 rounded-xl font-semibold hover:bg-green-600 hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-md text-lg"
            >
              <Phone size={24} />
              <span>Call Andre</span>
            </a>

            <a
              href="mailto:Andre@CTCWaterworks.com"
              className="w-full bg-white border-2 border-green-600 text-green-600 px-8 py-5 rounded-xl font-semibold hover:bg-green-600 hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-md text-lg"
            >
              <Mail size={24} />
              <span>Email Andre</span>
            </a>

            <a
              href="https://www.linkedin.com/in/andre-savala-180374157/"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-white border-2 border-green-600 text-green-600 px-8 py-5 rounded-xl font-semibold hover:bg-green-600 hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-3 shadow-md text-lg"
            >
              <Linkedin size={24} />
              <span>Connect on LinkedIn</span>
            </a>
          </motion.div>

          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-200 mb-12"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About Andre</h2>
            <p className="text-gray-700 leading-relaxed">
              Andre serves as a frontline contact for growers, helping them navigate sulfur burner, hydrocooler,
              and filtration questions with a customer-first mindset. With extensive experience in the
              agricultural equipment industry and a passion for finding the right fit for each operation, Andre
              brings real-world knowledge and dedicated support to every project.
            </p>
          </motion.div>

          {/* CTC Links Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-gray-200"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Explore CTC Solutions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/sulfur-burners"
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg border border-gray-200 hover:border-green-600 transition-all duration-300 group"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                  Sulfur Burners
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Improve water quality and soil pH management
                </p>
                <div className="flex items-center text-green-600 text-sm font-medium">
                  <span>Learn More</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </Link>

              <Link
                to="/hydrocoolers"
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg border border-gray-200 hover:border-green-600 transition-all duration-300 group"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                  Hydrocoolers
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Preserve freshness and extend shelf life
                </p>
                <div className="flex items-center text-green-600 text-sm font-medium">
                  <span>Learn More</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </Link>

              <Link
                to="/filtration"
                className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg border border-gray-200 hover:border-green-600 transition-all duration-300 group"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">
                  Filtration Systems
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Protect irrigation systems and equipment
                </p>
                <div className="flex items-center text-green-600 text-sm font-medium">
                  <span>Learn More</span>
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={16} />
                </div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AndrePage;
